// components/dashboard/AddUnit/UnitsListTable/UnitsListTable.tsx - Оновлено під нову архітектуру
import React from 'react';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIBadge from '@/components/shared/UIBadge';
import UIIconButton from '@/components/shared/UIIconButton';
import TenantActionButtons from '@/components/shared/TenantActionButtons';
import { UnitWithLeaseInfo } from '@/types';
import { ReactComponent as SortArrowUpIcon } from '@/img/sort-arrow-up.svg';
import { ReactComponent as SortArrowDownIcon } from '@/img/sort-arrow-down.svg';
import { ReactComponent as SeparatorIcon } from '@/img/separator-dashed.svg';
import { ReactComponent as DotsIcon } from '@/img/dots.svg';
import { safeToLowerCase } from '@/utils/stringUtils'; // Import safeToLowerCase
import './UnitsListTable.scss';

export interface UnitsListTableProps {
  data?: UnitWithLeaseInfo[];
  onSort?: (field: keyof UnitWithLeaseInfo) => void;
  onUnitAction?: (unitId: string, action: string, unitNumber?: string) => void;
}

const baseClass = 'UnitsListTable';

const mockHandlers = {
  onSort: (field: keyof UnitWithLeaseInfo) => console.log('Sort by:', field),
  onUnitAction: (unitId: string, action: string) =>
    console.log('Action:', action, 'for unit:', unitId),
};

const UnitsListTable: React.FC<UnitsListTableProps> = ({
  data = [],
  onSort = mockHandlers.onSort,
  onUnitAction = mockHandlers.onUnitAction,
}) => {
  const renderSortIcon = (field: keyof UnitWithLeaseInfo) => (
    <div className={`${baseClass}__sort`} onClick={() => onSort(field)}>
      <SeparatorIcon className={`${baseClass}__separator`} />
      <div className={`${baseClass}__arrows`}>
        <SortArrowUpIcon
          className={`${baseClass}__arrow ${baseClass}__arrow--up`}
        />
        <SortArrowDownIcon
          className={`${baseClass}__arrow ${baseClass}__arrow--down`}
        />
      </div>
    </div>
  );

  // Визначення статусу юніта на базі нової lease структури
  const getUnitStatus = (unit: UnitWithLeaseInfo) => {
    if (!unit.lease) {
      return { type: 'vacant' as const };
    }

    if (unit.lease.status === 'active') {
      return {
        type: 'active' as const,
        lease: unit.lease,
        tenant: unit.tenant,
      };
    }

    if (unit.lease.status === 'pending') {
      return {
        type: 'pending' as const,
        lease: unit.lease,
      };
    }

    return { type: 'vacant' as const };
  };

  // Badge для статусу юніта (замість vacancy)
  const getStatusBadge = (unit: UnitWithLeaseInfo) => {
    const status = getUnitStatus(unit);

    switch (status.type) {
      case 'active':
        return (
          <UIBadge variant="success" shape="pill">
            LEASED
          </UIBadge>
        );
      case 'pending':
        return (
          <UIBadge variant="warning" shape="pill">
            PENDING
          </UIBadge>
        );
      case 'vacant':
        return (
          <UIBadge variant="info" shape="pill">
            VACANT
          </UIBadge>
        );
      default:
        return (
          <UIBadge variant="secondary" shape="pill">
            UNKNOWN
          </UIBadge>
        );
    }
  };

  const getPaymentBadge = (status: UnitWithLeaseInfo['paymentStatus']) => {
    switch (status) {
      case 'paid':
        return (
          <UIBadge variant="success" shape="pill">
            PAID
          </UIBadge>
        );
      case 'debt':
        return (
          <UIBadge variant="danger" shape="pill">
            DEBT
          </UIBadge>
        );
      case 'not-set':
        return <span className={`${baseClass}__tenant-placeholder`}>-</span>;
      default:
        return (
          <UIBadge variant="secondary" shape="pill">
            UNKNOWN
          </UIBadge>
        );
    }
  };

  const getUnitTypeBadgeClass = (type: UnitWithLeaseInfo['type'] | null | undefined): string => {
    const baseBadgeClass = `${baseClass}__type-badge`;
    if (!type) {
      return `${baseBadgeClass} ${baseBadgeClass}--no-type`;
    }

    const modifiers: { [key: string]: string } = {
      residential: `${baseBadgeClass}--residential`,
      commercial: `${baseBadgeClass}--commercial`,
      mixed: `${baseBadgeClass}--mixed`,
      industrial: `${baseBadgeClass}--industrial`,
    };

    const lowercasedType = safeToLowerCase(type);
    const modifier = modifiers[lowercasedType];

    return modifier ? `${baseBadgeClass} ${modifier}` : `${baseBadgeClass} ${baseBadgeClass}--residential`; // Default to residential if no specific modifier
  };

  // Конфігурація стовпців
  const columns: SimpleTableColumn[] = [
    {
      key: 'number',
      label: 'NUMBER',
      width: '114px',
      headerClassName: `${baseClass}__header-cell--number`,
      className: `${baseClass}__cell--number`,
      headerContent: (
        <>
          <span>NUMBER</span>
          {renderSortIcon('number')}
        </>
      ),
      render: (number) => (
        <div className={`${baseClass}__unit-number`}>{number}</div>
      ),
    },
    {
      key: 'type',
      label: 'TYPE',
      width: '100px',
      headerContent: <span>TYPE</span>,
      render: (_, unit: UnitWithLeaseInfo) => (
        <div className={getUnitTypeBadgeClass(unit.type)}>{unit.type || 'N/A'}</div>
      ),
    },
    {
      key: 'status', // Замінено з 'vacancy' на 'status'
      label: 'STATUS',
      width: '121px',
      headerClassName: `${baseClass}__header-cell--vacancy`,
      className: `${baseClass}__cell--vacancy`,
      headerContent: (
        <>
          <span>STATUS</span>
        </>
      ),
      render: (_, unit: UnitWithLeaseInfo) => (
        <div className={`${baseClass}__vacancy-status`}>
          {getStatusBadge(unit)}
        </div>
      ),
    },
    {
      key: 'tenant',
      label: 'TENANT',
      width: '280px',
      headerClassName: `${baseClass}__header-cell--tenant`,
      className: `${baseClass}__cell--tenant`,
      headerContent: <span>TENANT</span>,
      render: (_, unit: UnitWithLeaseInfo) => {
        const status = getUnitStatus(unit);

        if (status.type === 'active' && status.tenant) {
          return (
            <div className={`${baseClass}__tenant-content`}>
              <UITenantInfo
                name={status.tenant.name}
                email={status.tenant.email}
                tenantId={status.tenant.id}
                avatar={status.tenant.avatar || null}
              />
            </div>
          );
        }

        if (status.type === 'pending' && status.lease) {
          // Для pending lease показуємо email з invite
          const displayEmail = status.lease.inviteEmail || 'Pending invitation';
          return (
            <div className={`${baseClass}__tenant-content`}>
              <div style={{ color: '#F57C00', fontSize: '14px' }}>
                Invited: {displayEmail}
              </div>
            </div>
          );
        }

        return <span className={`${baseClass}__tenant-placeholder`}>-</span>;
      },
    },
    {
      key: 'rent',
      label: 'RENT',
      width: '140px',
      headerClassName: `${baseClass}__header-cell--rent`,
      className: `${baseClass}__cell--rent`,
      headerContent: <span>RENT</span>,
      render: (_, unit: UnitWithLeaseInfo) => {
        const status = getUnitStatus(unit);

        if ((status.type === 'active' || status.type === 'pending') && status.lease) {
          return (
            <div className={`${baseClass}__rent-info`}>
              <span className={`${baseClass}__rent-info--amount`}>
                $ {status.lease.monthlyRent}
              </span>
              <span className={`${baseClass}__rent-info--period`}>
                per month
              </span>
            </div>
          );
        }

        return <span className={`${baseClass}__tenant-placeholder`}>-</span>;
      },
    },
    {
      key: 'payment',
      label: 'PAYMENT',
      width: '121px',
      headerClassName: `${baseClass}__header-cell--payment`,
      className: `${baseClass}__cell--payment`,
      headerContent: <span>PAYMENT</span>,
      render: (_, unit: UnitWithLeaseInfo) => (
        <div className={`${baseClass}__payment-status`}>
          {getPaymentBadge(unit.paymentStatus)}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '174px',
      headerClassName: `${baseClass}__header-cell--actions`,
      className: `${baseClass}__cell--actions`,
      headerContent: <></>,
      render: (_, unit: UnitWithLeaseInfo) => {
        const status = getUnitStatus(unit);

        return (
          <div className={`${baseClass}__actions-group`}>
            {status.type === 'vacant' ? (
              // Для вільних юнітів - кнопка створення lease
              <UIIconButton
                variant="primary"
                size="small"
                iconType="adduser"
                onClick={() =>
                  onUnitAction(unit.id.toString(), 'create-lease', unit.number)
                }
                title="Create lease"
              />
            ) : status.type === 'pending' ? (
              // Для pending lease - кнопка скасування
              <UIIconButton
                variant="danger"
                size="small"
                iconType="delete"
                onClick={() =>
                  onUnitAction(unit.id.toString(), 'revoke-invite', unit.number) // Змінено на 'revoke-invite'
                }
                title="Cancel pending lease"
              />
            ) : (
              // Для зайнятих юнітів - кнопки для роботи з tenant
              <TenantActionButtons
                tenantId={status.tenant?.id?.toString() || unit.id.toString()}
                actions={['view', 'edit']}
                onAction={(tenantId, action) =>
                  onUnitAction(unit.id.toString(), `tenant-${action}`)
                }
              />
            )}

            {/* Завжди показуємо кнопку інформації про юніт */}
            <UIIconButton
              variant="info"
              size="small"
              iconType="info"
              onClick={() => onUnitAction(unit.id.toString(), 'info')}
              title="Unit information"
            />
          </div>
        );
      },
    },
    {
      key: 'menu',
      label: 'Menu',
      width: '48px',
      headerClassName: `${baseClass}__header-cell--menu`,
      className: `${baseClass}__cell--menu`,
      headerContent: <DotsIcon className={`${baseClass}__menu-dots`} />,
      render: () => <DotsIcon className={`${baseClass}__menu-dots`} />,
    },
  ];

  return (
    <UISimpleTable
      data={data}
      columns={columns}
      className={baseClass}
      getRowKey={(unit) => unit.id.toString()}
    />
  );
};

export default UnitsListTable;